/**
 * CreativeMetaSummary — the creative's meta block rendered beside media in the
 * lightbox (mock 07 right panel): template/angle provenance, per-slot claim
 * chips, claim-safety summary and the persisted dataUsage audit rows.
 *
 * Single source of the claim-slot logic (extracted from CreativeDetailDrawer
 * in FR1 — the drawer imports CLAIM_FIELDS/claimStateForField from here).
 */

import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { AdCreativeDataUsageItem, AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_LIGHTBOX } from '../../selectors';
import { INSIGHT_CATEGORY_COLOR } from '../../services/AdCreativeInsightContextClient';
import type { AdCreativeAngle } from './batchModel';

export type CopyFieldKey = 'headline' | 'primaryText' | 'description' | 'cta';

/** dataUsage slot names (snake_case, agent output) -> copy field keys. */
export const SLOT_TO_FIELD: Record<string, CopyFieldKey> = {
  primary_text: 'primaryText',
  headline: 'headline',
  description: 'description',
  cta: 'cta',
};

/** Claim slots the safety chips account for (mirror of backend CLAIM_SLOTS). */
export const CLAIM_FIELDS: CopyFieldKey[] = ['primaryText', 'headline', 'description'];

export function claimStateForField(
  field: CopyFieldKey,
  dataUsage: AdCreativeDataUsageItem[],
  blocked: boolean,
): 'sourced' | 'inferred' | 'blocked' {
  const sourced = dataUsage.some((entry) => entry.slot && SLOT_TO_FIELD[entry.slot] === field);
  if (sourced) return 'sourced';
  return blocked ? 'blocked' : 'inferred';
}

export function CreativeMetaSummary({
  item,
  angle,
}: {
  item: AdCreativeItem;
  angle?: AdCreativeAngle | null;
}): React.ReactElement {
  const dataUsage = item.dataUsage ?? [];
  const blocked = (item.claimSafety?.blockedCount ?? 0) > 0;

  return (
    <Stack spacing={1.5} data-testid={AD_CREATIVE_LIGHTBOX.metaRoot}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Creative details</Typography>
        <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_LIGHTBOX.metaTemplate} sx={{ display: 'block' }}>
          {`template: ${item.templateId ?? '—'}`}
        </Typography>
        {angle?.label && (
          <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_LIGHTBOX.metaAngle} sx={{ display: 'block' }}>
            {`angle: ${angle.label}`}
          </Typography>
        )}
      </Box>

      {item.claimSafety && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '.05em' }}>
            Claim safety
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {CLAIM_FIELDS.map((field) => {
              const state = claimStateForField(field, dataUsage, blocked);
              return (
                <Chip
                  key={field}
                  size="small"
                  data-testid={AD_CREATIVE_LIGHTBOX.metaClaimChip(field)}
                  label={`${field} · ${state}`}
                  color={state === 'sourced' ? 'success' : state === 'blocked' ? 'error' : 'default'}
                  sx={{ height: 20, fontSize: 11 }}
                />
              );
            })}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={AD_CREATIVE_LIGHTBOX.metaClaimSummary}
            sx={{ display: 'block', mt: 0.5 }}
          >
            {`Claim safety: ${item.claimSafety.sourcedCount ?? 0} sourced · ${item.claimSafety.inferredCount ?? 0} inferred · ${item.claimSafety.blockedCount ?? 0} blocked`}
          </Typography>
        </Box>
      )}

      {dataUsage.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '.05em' }}>
            {`Data sources · ${dataUsage.length}`}
          </Typography>
          <Stack spacing={0.75}>
            {dataUsage.map((entry, index) => (
              <Box
                key={`${entry.sourcePath}-${entry.slot ?? index}`}
                data-testid={AD_CREATIVE_LIGHTBOX.metaSourceRow(index)}
                sx={{ borderLeft: 3, borderColor: 'info.main', bgcolor: 'action.hover', borderRadius: '0 8px 8px 0', px: 1.25, py: 0.75 }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {entry.category && (
                    <Chip
                      size="small"
                      color={INSIGHT_CATEGORY_COLOR[entry.category] ?? 'info'}
                      label={entry.category}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {entry.sourcePath}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>{entry.justification}</Typography>
                {entry.value && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    “{entry.value}”
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
